import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Box, Typography, Alert, CircularProgress, Button,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { color, radius } from '../../theme/tokens';

/**
 * Standard dialog shell for create/edit forms. Keeps the title bar, scrollable
 * body and action row consistent across every module.
 */
const FormDialog = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  maxWidth = 'sm',
  fullWidth = true,
  children,
  actions,
  onSubmit,
  onSave,
  submitLabel = 'Save',
  loading = false,
  saving = false,
  error,
  dividers = true,
  disableClose = false,
  sx,
}) => {
  const isBusy = saving || loading;

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const handler = onSubmit || onSave;
    if (handler) handler(e);
  };

  return (
    <Dialog
      open={open}
      onClose={disableClose || isBusy ? undefined : onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      sx={sx}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: subtitle ? 1 : 2 }}>
        {icon && (
          <Box
            sx={{
              width: 34, height: 34, flexShrink: 0,
              borderRadius: `${radius.md}px`,
              backgroundColor: color.primary[50],
              color: color.primary[600],
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              '& svg': { fontSize: 18 },
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: color.ink[900], lineHeight: 1.4 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: '0.8125rem', color: color.ink[500], fontWeight: 400, mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} disabled={isBusy} aria-label="Close dialog" sx={{ mt: -0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', m: 0 }}>
        <DialogContent dividers={dividers} sx={{ pt: error ? 2 : 1 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {children}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          {actions ?? (
            <>
              <Button onClick={onClose} disabled={isBusy} variant="outlined" color="inherit">
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isBusy}
              >
                {isBusy ? <CircularProgress size={18} color="inherit" /> : submitLabel}
              </Button>
            </>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default FormDialog;
